import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, Plus, Edit, Trash, RefreshCw, Save, CheckCircle2, Copy, Upload, FileSpreadsheet, Sun } from 'lucide-react';
import { toast } from 'react-toastify';
import { HOLIDAY_SCOPES, HOLIDAY_TYPES, listWorkingDays, validateHolidayInput } from '../payroll/core/leaveHoliday';
import {
    addSolarHolidays, cloneHolidayYear, confirmHolidays, deleteHoliday, importHolidays, loadHolidayData, saveHoliday,
} from '../payroll/data/leaveActions';
import { PAYROLL_PATHS, canManagePayroll, canViewPayrollPage } from '../payroll/data/permissions';
import { downloadWorkbook, parseExcelDate, readFirstSheet } from '../payroll/components/excelIO';
import { parseSheetNumber } from '../payroll/data/sheetFormat';
import {
    AccessDenied, Badge, ConfirmDialog, EmptyState, Field, FilterField, IconAction, ListToolbar, MessageList, Modal, NumberInput, PageShell,
    Pagination, SearchSelect, SortableTh, Spinner, StatCards, buttonPrimary, buttonSecondary, employeeOptions, formatDay,
    fromInputDate, inputClass, matchesSearch, tableClass, tbodyClass, tdClass, thClass, theadClass, toInputDate, usePagination, useSortedRows,
} from '../payroll/components/ui';

const IMPORT_HEADER = ['MÃ NGÀY LỄ', 'TÊN NGÀY LỄ', 'TỪ NGÀY (DD/MM/YYYY)', 'ĐẾN NGÀY (DD/MM/YYYY)', 'SỐ NGÀY HƯỞNG LƯƠNG', 'LOẠI NGÀY', 'PHẠM VI', 'TỔ', 'SỐ HĐ', 'MÔ TẢ'];

const scopeLabel = (holiday, employeeBySoHd) => {
    if (holiday.PHAM_VI === 'TO') return `Tổ ${holiday.TO}`;
    if (holiday.PHAM_VI === 'NHAN_SU') return employeeBySoHd.get(holiday.NHAN_SU_ID)?.HO_TEN || holiday.NHAN_SU_ID;
    return HOLIDAY_SCOPES.TOAN_CONG_TY;
};

function HolidayForm({ initial, isNew, data, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    // Số ngày hưởng lương tự theo số ngày làm việc của khoảng ngày, cho tới khi người dùng tự sửa.
    const [daysTouched, setDaysTouched] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const from = fromInputDate(form.from);
    const to = fromInputDate(form.to) || from;
    const set = (patch) => setForm((prev) => {
        const next = { ...prev, ...patch };
        if (!daysTouched && ('from' in patch || 'to' in patch)) {
            const start = fromInputDate(next.from);
            const end = fromInputDate(next.to) || start;
            if (start && end && end >= start) next.days = String(listWorkingDays(start, end).length);
        }
        return next;
    });
    const holiday = {
        ...initial.original, ID: form.ID, MA_NGAY_LE: form.MA_NGAY_LE, TEN_NGAY_LE: form.TEN_NGAY_LE, TU_NGAY: from, DEN_NGAY: to,
        SO_NGAY_HUONG_LUONG: parseSheetNumber(form.days, NaN), LOAI_NGAY: form.LOAI_NGAY, PHAM_VI: form.PHAM_VI, TO: form.TO, NHAN_SU_ID: form.NHAN_SU_ID, MO_TA: form.MO_TA,
    };
    const errors = validateHolidayInput(holiday);

    const handleSave = async () => {
        try {
            setSaving(true);
            await saveHoliday(holiday, { isNew, confirm: form.confirm, existing: data.holidays });
            toast.success(isNew ? 'Đã thêm ngày lễ' : 'Đã cập nhật ngày lễ');
            onSaved();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title={isNew ? 'Thêm ngày lễ' : 'Cập nhật ngày lễ'} subtitle="Ngày lễ đã xác nhận, hưởng lương được tự tính vào ngày lễ (NL) của kỳ lương" icon={CalendarDays} onClose={onClose} disabled={saving} size="max-w-3xl"
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={saving}>Hủy</button>
                    <button className={buttonPrimary} onClick={handleSave} disabled={saving || errors.length > 0}><Save className="w-4 h-4" />{saving ? 'Đang lưu...' : 'Lưu'}</button>
                </>
            )}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Tên ngày lễ" required><input className={inputClass} value={form.TEN_NGAY_LE} onChange={(e) => set({ TEN_NGAY_LE: e.target.value })} placeholder="Ví dụ Tết Nguyên đán" /></Field>
                <Field label="Mã ngày lễ" hint="Bỏ trống để tự đặt theo ngày"><input className={inputClass} value={form.MA_NGAY_LE} onChange={(e) => set({ MA_NGAY_LE: e.target.value.toUpperCase() })} placeholder="Ví dụ TET_AL_2027" /></Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Từ ngày" required><input type="date" className={inputClass} value={form.from} onChange={(e) => set({ from: e.target.value })} /></Field>
                    <Field label="Đến ngày" hint="Bỏ trống nếu chỉ một ngày"><input type="date" className={inputClass} value={form.to} onChange={(e) => set({ to: e.target.value })} /></Field>
                </div>
                <Field label="Số ngày hưởng lương" required hint="Không tính Chủ nhật. Ví dụ Tết nghỉ 7 ngày nhưng chỉ 5 ngày hưởng lương thì nhập 5.">
                    <NumberInput value={form.days} onChange={(next) => { setDaysTouched(true); set({ days: next }); }} placeholder="Ví dụ 1" />
                </Field>
                <Field label="Loại ngày">
                    <select className={inputClass} value={form.LOAI_NGAY} onChange={(e) => set({ LOAI_NGAY: e.target.value })}>
                        {Object.entries(HOLIDAY_TYPES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                </Field>
                <Field label="Phạm vi áp dụng">
                    <select className={inputClass} value={form.PHAM_VI} onChange={(e) => set({ PHAM_VI: e.target.value })}>
                        {Object.entries(HOLIDAY_SCOPES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                </Field>
                {form.PHAM_VI === 'TO' && (
                    <Field label="Tổ" required>
                        <select className={inputClass} value={form.TO} onChange={(e) => set({ TO: e.target.value })}>
                            <option value="">Chọn tổ</option>
                            {data.teams.map((team) => <option key={team} value={team}>{team}</option>)}
                        </select>
                    </Field>
                )}
                {form.PHAM_VI === 'NHAN_SU' && (
                    <Field label="Nhân sự" required>
                        <SearchSelect options={employeeOptions(data.employees)} value={form.NHAN_SU_ID} onChange={(value) => set({ NHAN_SU_ID: value })} placeholder="Tìm tên hoặc Số HĐ..." maxHeight="max-h-40" />
                    </Field>
                )}
                <div className="md:col-span-2">
                    <Field label="Mô tả"><textarea rows={2} className={inputClass} value={form.MO_TA} onChange={(e) => set({ MO_TA: e.target.value })} /></Field>
                </div>
                <label className="md:col-span-2 flex items-start gap-2 text-sm text-gray-700">
                    <input type="checkbox" className="mt-0.5" checked={form.confirm} onChange={(e) => set({ confirm: e.target.checked })} />
                    <span><b>Đã xác nhận</b>: dùng ngày lễ này để tính lương. Bỏ tick nếu còn chờ công ty chốt lịch nghỉ.</span>
                </label>
            </div>
            {errors.length > 0 && (form.TEN_NGAY_LE || form.from) && <div className="mt-4"><MessageList items={errors} /></div>}
        </Modal>
    );
}

function ImportModal({ data, onClose, onDone }) {
    const [rows, setRows] = useState(null);
    const [saving, setSaving] = useState(false);
    const employeeBySoHd = new Map(data.employees.map((employee) => [employee.SO_HD, employee]));
    const teamKeys = new Set(data.teams);

    const handleFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        try {
            const raw = await readFirstSheet(file);
            setRows(raw.filter((row) => String(row['TÊN NGÀY LỄ'] ?? '').trim() || row['TỪ NGÀY (DD/MM/YYYY)']).map((row, index) => {
                const from = parseExcelDate(row['TỪ NGÀY (DD/MM/YYYY)'] ?? row['TỪ NGÀY']);
                const to = parseExcelDate(row['ĐẾN NGÀY (DD/MM/YYYY)'] ?? row['ĐẾN NGÀY']);
                const scope = String(row['PHẠM VI'] || 'TOAN_CONG_TY').trim().toUpperCase();
                const type = String(row['LOẠI NGÀY'] || 'HUONG_LUONG').trim().toUpperCase();
                const holiday = {
                    MA_NGAY_LE: String(row['MÃ NGÀY LỄ'] ?? '').trim(), TEN_NGAY_LE: String(row['TÊN NGÀY LỄ'] ?? '').trim(),
                    TU_NGAY: from || null, DEN_NGAY: to || from || null, SO_NGAY_HUONG_LUONG: parseSheetNumber(row['SỐ NGÀY HƯỞNG LƯƠNG'], 1),
                    LOAI_NGAY: type, PHAM_VI: scope, TO: String(row['TỔ'] ?? '').trim(), NHAN_SU_ID: String(row['SỐ HĐ'] ?? '').trim(), MO_TA: String(row['MÔ TẢ'] ?? ''),
                };
                const errors = [...validateHolidayInput(holiday)];
                if (from === undefined || to === undefined) errors.push('Ngày phải dạng DD/MM/YYYY.');
                if (!HOLIDAY_TYPES[type]) errors.push(`LOẠI NGÀY phải là ${Object.keys(HOLIDAY_TYPES).join(' hoặc ')}.`);
                if (!HOLIDAY_SCOPES[scope]) errors.push(`PHẠM VI phải là ${Object.keys(HOLIDAY_SCOPES).join(', ')}.`);
                if (scope === 'TO' && holiday.TO && !teamKeys.has(holiday.TO)) errors.push(`Không có tổ ${holiday.TO} trong Phân bổ nhân sự.`);
                if (scope === 'NHAN_SU' && holiday.NHAN_SU_ID && !employeeBySoHd.has(holiday.NHAN_SU_ID)) errors.push(`Không có nhân sự Số HĐ ${holiday.NHAN_SU_ID}.`);
                return { line: index + 2, holiday, errors };
            }));
        } catch (error) {
            toast.error(error.message);
        }
    };
    const valid = rows?.filter((row) => !row.errors.length) ?? [];
    const handleImport = async () => {
        try {
            setSaving(true);
            const result = await importHolidays(data.holidays, valid.map((row) => row.holiday));
            toast.success(`Đã nhập ${result.created} ngày lễ (chờ xác nhận)${result.skipped ? `, bỏ ${result.skipped} dòng trùng ngày` : ''}`);
            onDone();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title="Nhập lịch ngày lễ từ Excel" icon={Upload} onClose={onClose} disabled={saving} size="max-w-3xl"
            footer={(
                <>
                    <button className={buttonSecondary} onClick={onClose} disabled={saving}>Đóng</button>
                    <button className={buttonPrimary} onClick={handleImport} disabled={saving || !valid.length}><Upload className="w-4 h-4" />Nhập {valid.length} dòng</button>
                </>
            )}>
            <div className="space-y-3 text-sm">
                <p className="text-gray-600">Dùng file mẫu (nút <b>Tải file mẫu</b>). Ngày ghi dạng DD/MM/YYYY. Ngày lễ nhập vào ở trạng thái <b>chờ xác nhận</b>.</p>
                <input type="file" accept=".xlsx,.xls" onChange={handleFile} className="text-sm" />
                {rows && (
                    <div className="border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-gray-50 sticky top-0"><tr><th className="px-2 py-1.5 text-left">Dòng</th><th className="px-2 py-1.5 text-left">Ngày lễ</th><th className="px-2 py-1.5 text-left">Thời gian</th><th className="px-2 py-1.5 text-left">Kết quả</th></tr></thead>
                            <tbody className="divide-y divide-gray-100">
                                {rows.map((row) => (
                                    <tr key={row.line} className={row.errors.length ? 'bg-red-50' : ''}>
                                        <td className="px-2 py-1.5">{row.line}</td>
                                        <td className="px-2 py-1.5">{row.holiday.TEN_NGAY_LE}</td>
                                        <td className="px-2 py-1.5 whitespace-nowrap">{formatDay(row.holiday.TU_NGAY)} – {formatDay(row.holiday.DEN_NGAY)}</td>
                                        <td className={`px-2 py-1.5 ${row.errors.length ? 'text-red-700' : 'text-green-700'}`}>{row.errors.length ? row.errors.join(' ') : 'Hợp lệ'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Modal>
    );
}

const LichNgayLe = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState('ALL');
    const [scope, setScope] = useState('ALL');
    const [type, setType] = useState('ALL');
    const [confirmed, setConfirmed] = useState('ALL');
    const [search, setSearch] = useState('');
    const [form, setForm] = useState(null);
    const [dialog, setDialog] = useState(null);
    const canView = canViewPayrollPage(PAYROLL_PATHS.holidays);
    const canManage = canManagePayroll();

    const reload = useCallback(async () => {
        try {
            setLoading(true);
            setData(await loadHolidayData());
        } catch (error) {
            console.error('Lỗi tải lịch ngày lễ:', error);
            toast.error('Không tải được lịch ngày lễ. Kiểm tra bảng LICH_NGAY_LE đã có trên AppSheet chưa.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (canView) reload(); }, [canView, reload]);

    const employeeBySoHd = useMemo(() => new Map((data?.employees ?? []).map((employee) => [employee.SO_HD, employee])), [data]);
    const ofYear = useMemo(() => (data?.holidays ?? []).filter((holiday) => holiday.TU_NGAY?.getFullYear() === year || holiday.DEN_NGAY?.getFullYear() === year), [data, year]);
    const years = useMemo(() => {
        const now = new Date().getFullYear();
        return [...new Set([now - 1, now, now + 1, ...(data?.holidays ?? []).map((holiday) => holiday.TU_NGAY?.getFullYear()).filter(Boolean)])].sort((a, b) => b - a);
    }, [data]);
    const filtered = useMemo(() => ofYear.filter((holiday) => (month === 'ALL' || holiday.TU_NGAY.getMonth() + 1 === Number(month) || holiday.DEN_NGAY?.getMonth() + 1 === Number(month))
        && (scope === 'ALL' || holiday.PHAM_VI === scope)
        && (type === 'ALL' || holiday.LOAI_NGAY === type)
        && (confirmed === 'ALL' || (confirmed === 'YES') === Boolean(holiday.DA_XAC_NHAN))
        && matchesSearch(`${holiday.MA_NGAY_LE} ${holiday.TEN_NGAY_LE} ${holiday.TO} ${holiday.NHAN_SU_ID}`, search)), [ofYear, month, scope, type, confirmed, search]);
    const accessors = useMemo(() => ({
        name: (row) => row.TEN_NGAY_LE, from: (row) => row.TU_NGAY, days: (row) => row.SO_NGAY_HUONG_LUONG ?? 0,
        type: (row) => row.LOAI_NGAY, scope: (row) => row.PHAM_VI, status: (row) => (row.DA_XAC_NHAN ? 1 : 0),
    }), []);
    const { sorted, sort, toggleSort } = useSortedRows(filtered, accessors, { key: 'from', direction: 'asc' });
    const { pageRows, pagination } = usePagination(sorted);

    if (!canView) return <AccessDenied />;

    const pending = filtered.filter((holiday) => !holiday.DA_XAC_NHAN);
    const run = async (action) => {
        try {
            setBusy(true);
            await action();
            await reload();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setBusy(false);
        }
    };
    const openForm = (holiday, isNew) => setForm({
        isNew,
        initial: {
            original: holiday,
            ID: holiday.ID || '',
            MA_NGAY_LE: holiday.MA_NGAY_LE || '',
            TEN_NGAY_LE: holiday.TEN_NGAY_LE || '',
            from: toInputDate(holiday.TU_NGAY),
            to: holiday.DEN_NGAY && holiday.TU_NGAY && holiday.DEN_NGAY.getTime() !== holiday.TU_NGAY.getTime() ? toInputDate(holiday.DEN_NGAY) : '',
            days: holiday.SO_NGAY_HUONG_LUONG === undefined ? '1' : String(holiday.SO_NGAY_HUONG_LUONG),
            LOAI_NGAY: holiday.LOAI_NGAY || 'HUONG_LUONG',
            PHAM_VI: holiday.PHAM_VI || 'TOAN_CONG_TY',
            TO: holiday.TO || '',
            NHAN_SU_ID: holiday.NHAN_SU_ID || '',
            MO_TA: holiday.MO_TA || '',
            confirm: isNew ? true : Boolean(holiday.DA_XAC_NHAN),
        },
    });
    const downloadTemplate = () => downloadWorkbook('Mau_LichNgayLe.xlsx', [
        { name: 'Lịch ngày lễ', rows: [IMPORT_HEADER, [`TET_AL_${year}`, 'Tết Nguyên đán', `16/02/${year}`, `20/02/${year}`, 5, 'HUONG_LUONG', 'TOAN_CONG_TY', '', '', '']] },
        { name: 'Giải thích', rows: [['CỘT', 'GIÁ TRỊ'], ['LOẠI NGÀY', 'HUONG_LUONG (hưởng lương) hoặc KHONG_HUONG_LUONG'], ['PHẠM VI', 'TOAN_CONG_TY, TO (điền cột TỔ) hoặc NHAN_SU (điền cột SỐ HĐ)'], ['SỐ NGÀY HƯỞNG LƯƠNG', 'Không tính Chủ nhật; số lẻ gõ dấu phẩy, ví dụ 0,5']] },
    ]);

    const actions = (row) => {
        if (!canManage) return null;
        return (
            <div className="flex justify-center gap-1">
                {!row.DA_XAC_NHAN && <IconAction icon={CheckCircle2} title="Xác nhận (dùng để tính lương)" tone="green" onClick={() => run(async () => { await confirmHolidays([row]); toast.success('Đã xác nhận ngày lễ'); })} disabled={busy} />}
                <IconAction icon={Edit} title="Sửa" onClick={() => openForm(row, false)} disabled={busy} />
                <IconAction icon={Trash} title="Xóa" tone="red" onClick={() => setDialog({ type: 'delete', holiday: row })} disabled={busy} />
            </div>
        );
    };
    const statusBadge = (row) => (row.DA_XAC_NHAN
        ? <Badge tone="green" title={row.NGUOI_XAC_NHAN ? `${row.NGUOI_XAC_NHAN} · ${formatDay(row.NGAY_XAC_NHAN)}` : undefined}>Đã xác nhận</Badge>
        : <Badge tone="yellow">Chờ xác nhận</Badge>);
    const period = (row) => (row.DEN_NGAY && row.DEN_NGAY.getTime() !== row.TU_NGAY.getTime() ? `${formatDay(row.TU_NGAY)} – ${formatDay(row.DEN_NGAY)}` : formatDay(row.TU_NGAY));

    return (
        <PageShell icon={CalendarDays} title="Lịch ngày lễ" subtitle="Ngày lễ đã xác nhận, hưởng lương được tự tính vào ngày lễ (NL) của bảng lương"
            actions={(
                <>
                    <button className={buttonSecondary} onClick={reload} disabled={loading}><RefreshCw className="w-4 h-4" />Làm mới</button>
                    {canManage && (
                        <>
                            <button className={buttonSecondary} onClick={() => run(async () => { const result = await addSolarHolidays(data.holidays, year); toast.success(`Đã thêm ${result.created} ngày lễ dương lịch ${year}${result.skipped ? `, bỏ ${result.skipped} ngày đã có` : ''}. Kiểm tra rồi xác nhận.`); })} disabled={!data || busy}
                                title="Tết Dương lịch, 30/4, 1/5, Quốc khánh 2/9. Tết Nguyên đán và Giỗ tổ theo âm lịch nên thêm tay."><Sun className="w-4 h-4" />Ngày lễ dương lịch {year}</button>
                            <button className={buttonSecondary} onClick={() => setDialog({ type: 'clone' })} disabled={!data || busy}><Copy className="w-4 h-4" />Nhân bản từ năm trước</button>
                            <button className={buttonSecondary} onClick={downloadTemplate}><FileSpreadsheet className="w-4 h-4" />Tải file mẫu</button>
                            <button className={buttonSecondary} onClick={() => setDialog({ type: 'import' })} disabled={!data || busy}><Upload className="w-4 h-4" />Nhập Excel</button>
                            <button className={buttonPrimary} onClick={() => openForm({}, true)} disabled={!data || busy}><Plus className="w-4 h-4" />Thêm ngày lễ</button>
                        </>
                    )}
                </>
            )}>
            <StatCards active={confirmed} onSelect={setConfirmed} cards={[
                { key: 'total', label: `Ngày lễ năm ${year}`, value: ofYear.length, tone: 'indigo', filterValue: 'ALL' },
                { key: 'paid', label: 'Hưởng lương', value: ofYear.filter((row) => row.LOAI_NGAY === 'HUONG_LUONG').length, tone: 'green', caption: `${ofYear.filter((row) => row.LOAI_NGAY === 'HUONG_LUONG' && row.DA_XAC_NHAN).reduce((sum, row) => sum + (row.SO_NGAY_HUONG_LUONG || 0), 0)} ngày đã xác nhận` },
                { key: 'unpaid', label: 'Không hưởng lương', value: ofYear.filter((row) => row.LOAI_NGAY !== 'HUONG_LUONG').length, tone: 'blue' },
                { key: 'pending', label: 'Chờ xác nhận', value: ofYear.filter((row) => !row.DA_XAC_NHAN).length, tone: 'yellow', caption: 'Chưa dùng để tính lương', filterValue: 'NO' },
            ]} />
            <ListToolbar search={search} onSearch={setSearch} placeholder="Tìm mã, tên ngày lễ, tổ, số HĐ..."
                filterCount={[month, scope, type, confirmed].filter((value) => value !== 'ALL').length}
                onClearFilters={() => { setMonth('ALL'); setScope('ALL'); setType('ALL'); setConfirmed('ALL'); setSearch(''); }}
                extra={(
                    <select className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white" value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Năm">
                        {years.map((item) => <option key={item} value={item}>Năm {item}</option>)}
                    </select>
                )}
                filters={(
                    <>
                        <FilterField label="Tháng">
                            <select className={inputClass} value={month} onChange={(e) => setMonth(e.target.value)}>
                                <option value="ALL">Tất cả tháng</option>
                                {Array.from({ length: 12 }, (_, index) => <option key={index + 1} value={index + 1}>Tháng {index + 1}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Phạm vi">
                            <select className={inputClass} value={scope} onChange={(e) => setScope(e.target.value)}>
                                <option value="ALL">Tất cả phạm vi</option>
                                {Object.entries(HOLIDAY_SCOPES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Loại ngày">
                            <select className={inputClass} value={type} onChange={(e) => setType(e.target.value)}>
                                <option value="ALL">Tất cả loại</option>
                                {Object.entries(HOLIDAY_TYPES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </select>
                        </FilterField>
                        <FilterField label="Xác nhận">
                            <select className={inputClass} value={confirmed} onChange={(e) => setConfirmed(e.target.value)}>
                                <option value="ALL">Tất cả</option><option value="YES">Đã xác nhận</option><option value="NO">Chờ xác nhận</option>
                            </select>
                        </FilterField>
                    </>
                )} />
            {canManage && pending.length > 0 && (
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                    <span>{pending.length} ngày lễ đang chờ xác nhận nên chưa được tính vào lương.</span>
                    <button className={buttonSecondary} disabled={busy} onClick={() => run(async () => { const count = await confirmHolidays(pending); toast.success(`Đã xác nhận ${count} ngày lễ`); })}><CheckCircle2 className="w-4 h-4" />Xác nhận cả {pending.length}</button>
                </div>
            )}
            {loading || !data ? <Spinner /> : sorted.length === 0 ? (
                <EmptyState icon={CalendarDays} title={`Chưa có ngày lễ năm ${year}`}>Bấm <b>Ngày lễ dương lịch {year}</b>, <b>Nhân bản từ năm trước</b> hoặc <b>Thêm ngày lễ</b> (Tết Nguyên đán, Giỗ tổ theo âm lịch).</EmptyState>
            ) : (
                <>
                    <div className="space-y-3 md:hidden">
                        {pageRows.map((row) => (
                            <div key={row.ID} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex justify-between items-start gap-2">
                                    <div><p className="font-medium text-gray-900">{row.TEN_NGAY_LE}</p><p className="text-xs text-gray-500">{row.MA_NGAY_LE} · {period(row)}</p></div>
                                    {statusBadge(row)}
                                </div>
                                <p className="text-sm text-gray-700 mt-2">{row.SO_NGAY_HUONG_LUONG} ngày · {HOLIDAY_TYPES[row.LOAI_NGAY] || row.LOAI_NGAY} · {scopeLabel(row, employeeBySoHd)}</p>
                                <div className="flex justify-end mt-2">{actions(row)}</div>
                            </div>
                        ))}
                    </div>
                    <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-lg">
                        <table className={tableClass}>
                            <thead className={theadClass}>
                                <tr>
                                    <SortableTh label="Ngày lễ" sortKey="name" sort={sort} onSort={toggleSort} />
                                    <SortableTh label="Thời gian" sortKey="from" sort={sort} onSort={toggleSort} />
                                    <SortableTh label="Ngày hưởng lương" sortKey="days" sort={sort} onSort={toggleSort} align="right" />
                                    <SortableTh label="Loại" sortKey="type" sort={sort} onSort={toggleSort} />
                                    <SortableTh label="Phạm vi" sortKey="scope" sort={sort} onSort={toggleSort} />
                                    <SortableTh label="Trạng thái" sortKey="status" sort={sort} onSort={toggleSort} />
                                    <th className={`${thClass} text-center`}>Thao tác</th>
                                </tr>
                            </thead>
                            <tbody className={tbodyClass}>
                                {pageRows.map((row) => (
                                    <tr key={row.ID} className="hover:bg-gray-50 transition-colors">
                                        <td className={tdClass}><div className="font-medium text-gray-900">{row.TEN_NGAY_LE}</div><div className="text-xs text-gray-500 font-mono">{row.MA_NGAY_LE}</div>{row.MO_TA && <div className="text-xs text-gray-500">{row.MO_TA}</div>}</td>
                                        <td className={`${tdClass} whitespace-nowrap`}>{period(row)}</td>
                                        <td className={`${tdClass} text-right font-semibold`}>{row.SO_NGAY_HUONG_LUONG}</td>
                                        <td className={tdClass}><Badge tone={row.LOAI_NGAY === 'HUONG_LUONG' ? 'green' : 'gray'}>{HOLIDAY_TYPES[row.LOAI_NGAY] || row.LOAI_NGAY}</Badge></td>
                                        <td className={tdClass}>{scopeLabel(row, employeeBySoHd)}</td>
                                        <td className={tdClass}>{statusBadge(row)}</td>
                                        <td className={tdClass}>{actions(row)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination pagination={pagination} unit="ngày lễ" />
                </>
            )}
            {form && <HolidayForm {...form} data={data} onClose={() => setForm(null)} onSaved={() => { setForm(null); reload(); }} />}
            {dialog?.type === 'import' && <ImportModal data={data} onClose={() => setDialog(null)} onDone={() => { setDialog(null); reload(); }} />}
            {dialog?.type === 'clone' && (
                <ConfirmDialog title="Nhân bản lịch ngày lễ" danger={false} confirmLabel={`Nhân bản sang ${year}`} onClose={() => setDialog(null)}
                    message={<>Chép lịch ngày lễ năm <b>{year - 1}</b> sang năm <b>{year}</b>, giữ nguyên ngày/tháng, ở trạng thái chờ xác nhận. Ngày lễ âm lịch (Tết, Giỗ tổ) đổi ngày mỗi năm nên cần sửa lại sau khi chép.</>}
                    onConfirm={() => run(async () => { const result = await cloneHolidayYear(data.holidays, year - 1, year); setDialog(null); toast.success(`Đã chép ${result.created} ngày lễ sang ${year}${result.skipped ? `, bỏ ${result.skipped} ngày trùng` : ''}`); })} />
            )}
            {dialog?.type === 'delete' && (
                <ConfirmDialog title="Xóa ngày lễ" confirmLabel="Xóa" onClose={() => setDialog(null)}
                    message={<>Xóa ngày lễ <b>{dialog.holiday.TEN_NGAY_LE}</b> ({period(dialog.holiday)})? Kỳ lương chưa duyệt của tháng này sẽ cần chốt số lại.</>}
                    onConfirm={() => run(async () => { await deleteHoliday(dialog.holiday); setDialog(null); toast.success('Đã xóa ngày lễ'); })} />
            )}
        </PageShell>
    );
};

export default LichNgayLe;
